import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { updateTableData } from '../utilities/SliceDataHelper';
import { ReportApi } from '../../api/ReportApi';
import type { IReport } from '../../../types/IComponentTypes';
import type { IReportRequest } from '../../../types/IApiTypes';
import type { AppState } from '../AppStore';
import { RecordLength } from 'data/Constants';

/** Initial report history state, shared by every report type */
const initialState: IReport = {
  data: {},
  totalCount: 0,
  currentPage: 1,
  recordsPerPage: RecordLength[0],
  searchKey: '',
};

/**
 * Clear the cached history and fetch the paginated report history for the requested type
 *
 * @param action - Report request with page, limit, report type and optional search code
 * @returns Paginated report history, or an error object on failure
 */
export const getReports = createAsyncThunk(
  'report/getReports',
  async (action: IReportRequest, thunkAPI) => {
    thunkAPI.dispatch(resetReports());
    return ReportApi.getReport(action, thunkAPI.dispatch);
  }
);

/**
 * Trigger generation of a new descope list report
 *
 * @returns Success response data, or an error object on failure
 */
export const createReport = createAsyncThunk(
  'report/createReport',
  async (reportType: string, thunkAPI) => {
    return ReportApi.generateReport({ reportType }, thunkAPI.dispatch);
  }
);

/**
 * Redux slice storing the paginated report history table shared by all report types
 */
const reportSlice = createSlice({
  name: 'report',
  initialState,
  reducers: {
    /** Clear the cached report history so the next read starts from an empty table */
    resetReports: () => initialState,
  },
  extraReducers: (builder) => {
    builder.addCase(getReports.fulfilled, (state, action) => {
      if (!('error' in action.payload)) {
        updateTableData(state, action.payload);
      }
    });
  },
});

export const { resetReports } = reportSlice.actions;

/**
 * Selector for the report history state
 *
 * @param state - Root application state
 * @returns Report table state
 */
export const reports = (state: AppState) => state.reports;

export default reportSlice.reducer;
